import React, { useState, useEffect, useRef } from 'react';
import ReactECharts from 'echarts-for-react';
import { useFilter } from '../../../../features/filter/hook';
import styles from './EChartsView.module.scss';

export interface EChartsData {
  name: string;
  value: number | null;
  color?: string;
}

export interface EChartsSeries {
  name: string;
  data: EChartsData[];
  type?: 'line' | 'bar';
  color?: string;
}

export interface EChartsViewProps {
  data: EChartsData[] | EChartsSeries[];
  title?: string;
  type?: 'line' | 'bar' | 'pie' | 'gauge' | 'combined';
  height?: number;
  width?: number | string;
  showLegend?: boolean;
  showTooltip?: boolean;
  xAxisLabel?: string;
  yAxisLabel?: string;
  showDataZoom?: boolean;
}

export default function EChartsView({
  data,
  title = "Graphique",
  type = 'line',
  height = 400,
  width = '100%',
  showLegend = true,
  showTooltip = true,
  xAxisLabel = "Temps",
  yAxisLabel = "Valeur",
  showDataZoom = true
}: EChartsViewProps) {
  const [slideDirection, setSlideDirection] = useState<'left' | 'right' | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [previousData, setPreviousData] = useState<EChartsData[] | EChartsSeries[] | null>(null);
  const prevDataKeyRef = useRef<string>('');
  const prevDataRef = useRef<EChartsData[] | EChartsSeries[]>(data);
  const prevStartDateRef = useRef<string | null | undefined>(null);
  
  // Accéder au filtre pour détecter la direction
  const { filter } = useFilter();
  
  // Créer une clé unique basée sur les données pour détecter les changements
  const getDataKey = (data: EChartsData[] | EChartsSeries[]): string => {
    if (Array.isArray(data) && data.length > 0) {
      if ('data' in data[0]) {
        // EChartsSeries - prendre la première et dernière date de chaque série
        const series = data as EChartsSeries[];
        return series.map(s => {
          const first = s.data[0]?.name || '';
          const last = s.data[s.data.length - 1]?.name || '';
          return `${first}-${last}`;
        }).join('|');
      } else {
        // EChartsData - prendre les premières et dernières dates
        const dataArray = data as EChartsData[];
        const first = dataArray[0]?.name || '';
        const last = dataArray[dataArray.length - 1]?.name || '';
        return `${first}-${last}`;
      }
    }
    return '';
  };
  
  // Extraire la première date d'une clé pour comparer
  const getFirstDateFromKey = (key: string): number => {
    const firstPart = key.split('|')[0]?.split('-')[0] || '';
    const date = new Date(firstPart).getTime();
    return isNaN(date) ? 0 : date;
  };
  
  // Détecter les changements de données et déterminer la direction
  useEffect(() => {
    const currentDataKey = getDataKey(data);
    const prevDataKey = prevDataKeyRef.current;
    const currentStartDate = filter.startDate;
    const prevStartDate = prevStartDateRef.current;
    
    // Initialiser la référence au premier rendu
    if (!prevDataKey) {
      prevDataKeyRef.current = currentDataKey;
      prevDataRef.current = data;
      prevStartDateRef.current = currentStartDate;
      return;
    }
    
    if (currentDataKey && currentDataKey !== prevDataKey) {
      let direction: 'left' | 'right' = 'left'; // Par défaut
      
      // Utiliser la comparaison des dates startDate du filtre pour déterminer la direction
      if (currentStartDate && prevStartDate) {
        const currentDate = new Date(currentStartDate).getTime();
        const prevDate = new Date(prevStartDate).getTime();
        
        if (!isNaN(currentDate) && !isNaN(prevDate)) {
          // Next (période suivante) → startDate plus récente → glissement vers la gauche
          // Prev (période précédente) → startDate plus ancienne → glissement vers la droite
          direction = currentDate > prevDate ? 'left' : 'right';
        }
      } else {
        // Fallback : utiliser la comparaison des dates des données
        const currentDate = getFirstDateFromKey(currentDataKey);
        const prevDate = getFirstDateFromKey(prevDataKey);
        
        if (currentDate !== 0 && prevDate !== 0 && currentDate !== prevDate) {
          direction = currentDate > prevDate ? 'left' : 'right';
        }
      }
      
      // Sauvegarder les données précédentes depuis la référence
      setPreviousData(prevDataRef.current);
      setIsAnimating(true);
      setSlideDirection(direction);
      
      // Réinitialiser l'animation après la transition
      setTimeout(() => {
        setIsAnimating(false);
        setSlideDirection(null);
        setPreviousData(null);
      }, 500);
      
      // Mettre à jour les références
      prevDataKeyRef.current = currentDataKey;
      prevDataRef.current = data;
      prevStartDateRef.current = currentStartDate;
    } else {
      // Mettre à jour la référence des données même si la clé n'a pas changé
      prevDataRef.current = data;
      prevStartDateRef.current = currentStartDate;
    }
  }, [data, filter.startDate]);
  
  const getOption = (chartData: EChartsData[] | EChartsSeries[] = data) => {
    const baseOption = {
      title: {
        show: false // Le titre est affiché séparément pour rester fixe
      },
      tooltip: {
        show: showTooltip,
        trigger: 'axis',
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        textStyle: {
          color: '#fff'
        },
        formatter: (params: any) => {
          // Gérer le cas où params est un tableau (trigger: 'axis')
          if (Array.isArray(params) && params.length > 0) {
            let result = params[0].axisValue + '<br/>';
            params.forEach((param: any) => {
              const value = param.value;
              let formattedValue: string;
              if (value === null || value === undefined) {
                formattedValue = 'N/A';
              } else if (typeof value === 'number') {
                formattedValue = value.toFixed(2);
              } else {
                formattedValue = String(value);
              }
              const seriesName = param.seriesName || yAxisLabel || 'Valeur';
              result += `${param.marker}${seriesName}: ${formattedValue}<br/>`;
            });
            return result;
          }
          // Gérer le cas où params est un objet unique (trigger: 'item' pour pie, etc.)
          if (params && typeof params === 'object') {
            const value = params.value;
            let formattedValue: string;
            if (value === null || value === undefined) {
              formattedValue = 'N/A';
            } else if (typeof value === 'number') {
              formattedValue = value.toFixed(2);
            } else {
              formattedValue = String(value);
            }
            const itemName = params.name || yAxisLabel || 'Valeur';
            return `${params.marker}${itemName}: ${formattedValue}`;
          }
          return '';
        }
      },
      toolbox: {
        feature: {
          dataZoom: {
            yAxisIndex: 'none',
            title: {
              zoom: 'Zoom',
              back: 'Reset'
            }
          },
          restore: {
            title: 'Restaurer'
          },
          saveAsImage: {
            title: 'Sauvegarder'
          }
        },
        right: 0,
        top: -15,
        itemSize: 14,
        itemGap: 6
      },
      dataZoom: showDataZoom
        ? [
            {
              type: 'inside',
              start: 0,
              end: 100
            },
            {
              type: 'slider',
              start: 0,
              end: 100,
              height: 20,
              bottom: 20,
              handleStyle: {
                color: '#3b82f6'
              },
              textStyle: {
                color: '#6b7280'
              }
            }
          ]
        : [],
      legend: {
        show: showLegend,
        top: 'bottom',
        textStyle: {
          color: '#374151'
        }
      },
      grid: {
        left: '8%',
        right: '8%',
        bottom: '20%',
        top: '20%',
        containLabel: true
      }
    };

    // Vérifier si les données sont des séries multiples
    const isMultipleSeries = Array.isArray(chartData) && chartData.length > 0 && 'data' in chartData[0];
    
    // Type guard pour vérifier si c'est EChartsData[] (utilisé pour la validation des données)
    // const isEChartsData = (item: any): item is EChartsData => {
    //   return 'name' in item && 'value' in item && !('data' in item);
    // };

    switch (type) {
      case 'line':
        if (isMultipleSeries) {
          const seriesData = chartData as EChartsSeries[];
          const categories = seriesData[0]?.data.map((item) => item.name) ?? [];
          return {
            ...baseOption,
            xAxis: {
              type: 'category',
              data: categories,
              name: xAxisLabel,
              nameTextStyle: { color: '#6b7280' },
              axisLine: { lineStyle: { color: '#e5e7eb' } },
            },
            yAxis: {
              type: 'value',
              name: yAxisLabel,
              nameTextStyle: { color: '#6b7280' },
              axisLine: { lineStyle: { color: '#e5e7eb' } },
              splitLine: { lineStyle: { color: '#f3f4f6' } },
            },
            series: seriesData.map((series) => {
              const col = series.color || '#3b82f6';
              return {
                name: series.name,
                data: series.data.map((d) => d.value),
                type: 'line' as const,
                smooth: true,
                connectNulls: false,
                lineStyle: { color: col, width: 2 },
                itemStyle: { color: col },
              };
            }),
          };
        }

        const lineData = chartData as EChartsData[];
        return {
          ...baseOption,
          xAxis: {
            type: 'category',
            data: lineData.map(item => item.name),
            name: xAxisLabel,
            nameTextStyle: {
              color: '#6b7280'
            },
            axisLine: {
              lineStyle: {
                color: '#e5e7eb'
              }
            }
          },
          yAxis: {
            type: 'value',
            name: yAxisLabel,
            nameTextStyle: {
              color: '#6b7280'
            },
            axisLine: {
              lineStyle: {
                color: '#e5e7eb'
              }
            },
            splitLine: {
              lineStyle: {
                color: '#f3f4f6'
              }
            }
          },
          series: [{
            name: yAxisLabel,
            data: lineData.map(item => item.value),
            type: 'line',
            smooth: true,
            connectNulls: false,
            lineStyle: {
              color: '#3b82f6',
              width: 3
            },
            itemStyle: {
              color: '#3b82f6'
            },
            areaStyle: {
              color: {
                type: 'linear',
                x: 0,
                y: 0,
                x2: 0,
                y2: 1,
                colorStops: [{
                  offset: 0, color: 'rgba(59, 130, 246, 0.3)'
                }, {
                  offset: 1, color: 'rgba(59, 130, 246, 0.05)'
                }]
              }
            }
          }]
        };

      case 'bar':
        if (isMultipleSeries) {
          return baseOption; // Fallback pour les séries multiples
        }
        
        const barData = chartData as EChartsData[];
        return {
          ...baseOption,
          xAxis: {
            type: 'category',
            data: barData.map(item => item.name),
            name: xAxisLabel,
            nameTextStyle: {
              color: '#6b7280'
            },
            axisLine: {
              lineStyle: {
                color: '#e5e7eb'
              }
            }
          },
          yAxis: {
            type: 'value',
            name: yAxisLabel,
            nameTextStyle: {
              color: '#6b7280'
            },
            axisLine: {
              lineStyle: {
                color: '#e5e7eb'
              }
            },
            splitLine: {
              lineStyle: {
                color: '#f3f4f6'
              }
            }
          },
          series: [{
            name: yAxisLabel,
            data: barData.map(item => ({
              value: item.value,
              itemStyle: {
                color: item.color || '#10b981'
              }
            })),
            type: 'bar',
            barWidth: '60%'
          }]
        };

      case 'pie':
        if (isMultipleSeries) {
          return baseOption; // Fallback pour les séries multiples
        }
        
        const pieData = chartData as EChartsData[];
        return {
          ...baseOption,
          tooltip: {
            ...baseOption.tooltip,
            trigger: 'item'
          },
          series: [{
            data: pieData.map(item => ({
              name: item.name,
              value: item.value,
              itemStyle: {
                color: item.color
              }
            })),
            type: 'pie',
            radius: ['40%', '70%'],
            center: ['50%', '50%'],
            label: {
              show: true,
              formatter: '{b}: {c} ({d}%)'
            },
            emphasis: {
              itemStyle: {
                shadowBlur: 10,
                shadowOffsetX: 0,
                shadowColor: 'rgba(0, 0, 0, 0.5)'
              }
            }
          }]
        };

      case 'gauge':
        if (isMultipleSeries) {
          return baseOption; // Fallback pour les séries multiples
        }
        
        const gaugeData = chartData as EChartsData[];
        return {
          ...baseOption,
          tooltip: {
            ...baseOption.tooltip,
            trigger: 'item'
          },
          series: [{
            data: [{
              value: gaugeData[0]?.value || 0,
              name: gaugeData[0]?.name || 'Valeur'
            }],
            type: 'gauge',
            radius: '80%',
            startAngle: 200,
            endAngle: -20,
            min: 0,
            max: 100,
            splitNumber: 10,
            axisLine: {
              lineStyle: {
                width: 6,
                color: [
                  [0.3, '#ff6e76'],
                  [0.7, '#ffa500'],
                  [1, '#67e0e3']
                ]
              }
            },
            pointer: {
              itemStyle: {
                color: 'auto'
              }
            },
            axisTick: {
              distance: -30,
              splitNumber: 5,
              lineStyle: {
                width: 2,
                color: '#999'
              }
            },
            splitLine: {
              distance: -30,
              length: 30,
              lineStyle: {
                width: 4,
                color: '#999'
              }
            },
            axisLabel: {
              color: 'auto',
              distance: 40,
              fontSize: 12
            },
            detail: {
              valueAnimation: true,
              formatter: '{value}%',
              color: 'auto',
              fontSize: 20
            }
          }]
        };

      case 'combined':
        if (!isMultipleSeries) {
          return baseOption;
        }
        
        const seriesData = chartData as EChartsSeries[];
        const allDates = new Set<string>();
        
        // Collecter toutes les dates uniques
        seriesData.forEach(series => {
          series.data.forEach(item => allDates.add(item.name));
        });
        
        const sortedDates = Array.from(allDates).sort((a, b) => 
          new Date(a).getTime() - new Date(b).getTime()
        );
        
        return {
          ...baseOption,
          xAxis: {
            type: 'category',
            data: sortedDates,
            name: xAxisLabel,
            nameTextStyle: {
              color: '#6b7280'
            },
            axisLine: {
              lineStyle: {
                color: '#e5e7eb'
              }
            }
          },
          yAxis: [
            {
              type: 'value',
              name: 'Index (kWh)',
              nameTextStyle: {
                color: '#3b82f6'
              },
              axisLine: {
                lineStyle: {
                  color: '#3b82f6'
                }
              },
              axisLabel: {
                color: '#3b82f6'
              },
              splitLine: {
                lineStyle: {
                  color: '#f3f4f6'
                }
              }
            },
            {
              type: 'value',
              name: 'Consommation (kWh)',
              nameTextStyle: {
                color: '#10b981'
              },
              axisLine: {
                lineStyle: {
                  color: '#10b981'
                }
              },
              axisLabel: {
                color: '#10b981'
              },
              splitLine: {
                show: false
              }
            }
          ],
          series: seriesData.map((series, index) => {
            const seriesValues = sortedDates.map(date => {
              const dataPoint = series.data.find(item => item.name === date);
              return dataPoint?.value ?? null;
            });

            return {
              name: series.name,
              data: seriesValues,
              type: series.type || 'line',
              yAxisIndex: series.type === 'bar' ? 1 : 0, // Index sur axe gauche, Consommation sur axe droit
              smooth: series.type === 'line',
              connectNulls: false,
              lineStyle: series.type === 'line' ? {
                color: series.color || '#3b82f6',
                width: 3
              } : undefined,
              itemStyle: {
                color: series.color || (series.type === 'bar' ? '#10b981' : '#3b82f6')
              },
              barWidth: series.type === 'bar' ? '60%' : undefined,
              areaStyle: series.type === 'line' ? {
                color: {
                  type: 'linear',
                  x: 0,
                  y: 0,
                  x2: 0,
                  y2: 1,
                  colorStops: [{
                    offset: 0, color: `${series.color || '#3b82f6'}30`
                  }, {
                    offset: 1, color: `${series.color || '#3b82f6'}05`
                  }]
                }
              } : undefined
            };
          })
        };

      default:
        return baseOption;
    }
  };

  // Créer une clé unique pour forcer le re-render lors de l'animation
  const currentChartKey = `chart-current-${getDataKey(data)}`;
  const previousChartKey = previousData ? `chart-previous-${getDataKey(previousData)}` : null;
  
  const containerStyle = {
    height: typeof height === 'number' ? height + 48 + 40 : undefined, // +48 pour le padding (24px top + 24px bottom) + 40 pour le titre et espacement
  };

  // Calculer la hauteur réelle du graphique (sans le padding du conteneur)
  const chartHeight = typeof height === 'number' ? height : height;
  const chartWidth = width;
  const chartsWrapperStyle = {
    height: typeof height === 'number' ? height : 'auto',
  };

  return (
    <div className={styles.echartsContainer} style={containerStyle}>
      <div className={styles.chartTitle}>
        {title}
      </div>
      <div className={styles.chartsWrapper} style={chartsWrapperStyle}>
        {isAnimating && previousData && (
          <div className={`${styles.chartSlide} ${slideDirection === 'left' ? styles.slideOutLeft : styles.slideOutRight}`}>
            <ReactECharts
              option={getOption(previousData)}
              style={{ height: chartHeight, width: chartWidth }}
              opts={{ renderer: 'canvas' }}
              key={previousChartKey}
            />
          </div>
        )}
        <div className={`${isAnimating ? styles.chartSlide : styles.chartVisible} ${
          isAnimating 
            ? slideDirection === 'left' 
              ? styles.slideInLeft 
              : slideDirection === 'right'
              ? styles.slideInRight
              : ''
            : ''
        }`}>
          <ReactECharts
            option={getOption()}
            style={{ height: chartHeight, width: chartWidth }}
            opts={{ renderer: 'canvas' }}
            key={currentChartKey}
          />
        </div>
      </div>
    </div>
  );
}
